import { useCallback, useEffect, useRef } from "react";

import { FEATURE_PLUS, useEntitlementsStore } from "../stores/entitlements";

// Sill Plus design spec §4.1 — module-level cache, anti-flicker, enumerated
// refresh surfaces only. See `stores/entitlements.ts` for the rationale; this
// file is a thin selector + token-resolver harness layered on top, exactly
// mirroring `useReadiness`.

export type EntitlementsHeldBy = "self" | "household_member" | "none";

export type UseEntitlementsOptions = {
  // Either provide a stable Clerk token directly, or pass a `getToken`
  // resolver. Mirrors the pattern in useReadiness / useEstimate.
  token?: string;
  getToken?: () => Promise<string | null>;
  // Pass `enabled: false` to defer the fetch (e.g., before Clerk hydrates).
  enabled?: boolean;
};

export type UseEntitlementsState = {
  isPlus: boolean;
  heldBy: EntitlementsHeldBy;
  // True only when there is no cached data yet and a fetch is in flight.
  // Drives empty-state spinners — must NOT flip back to true on background
  // refreshes (anti-flicker per ADR-027).
  isLoading: boolean;
  // True when a fetch is in flight while cached data is already present.
  isRefreshing: boolean;
  // Imperatively trigger a revalidation. Only call from the enumerated
  // surfaces described in spec §4.1 (billing tab manual refresh, post-webhook
  // subscription transition). Page navigation must NOT call this.
  refetch: () => Promise<void>;
};

export function useEntitlements(
  opts: UseEntitlementsOptions = {},
): UseEntitlementsState {
  const { token, getToken, enabled = true } = opts;

  const data = useEntitlementsStore((s) => s.data);
  const isFetching = useEntitlementsStore((s) => s.isFetching);
  const hasLoaded = useEntitlementsStore((s) => s.hasLoaded);

  // Capture the latest token resolver in a ref so the load effect can run
  // exactly once per (enabled) mount without retriggering when the parent
  // re-renders with a new `getToken` identity — same fix as useReadiness.
  const resolverRef = useRef<() => Promise<string | null | undefined>>(
    () => Promise.resolve(undefined),
  );
  useEffect(() => {
    resolverRef.current = async () => {
      if (typeof getToken === "function") {
        return (await getToken()) ?? undefined;
      }
      return token;
    };
  }, [getToken, token]);

  useEffect(() => {
    if (!enabled) return;
    void useEntitlementsStore
      .getState()
      .ensureLoaded(() => resolverRef.current());
  }, [enabled]);

  const refetch = useCallback(async () => {
    await useEntitlementsStore
      .getState()
      .refresh(() => resolverRef.current());
  }, []);

  const plusStatus = data?.[FEATURE_PLUS];
  const isPlus = plusStatus?.granted === true;
  const heldByRaw = plusStatus?.held_by ?? "";
  // Backend uses `""` (or omits) for "no one holds it". The hook surfaces the
  // friendlier "none" so callers can render `${heldBy === "household_member"}`
  // branches without an undefined check.
  const heldBy: EntitlementsHeldBy = isPlus
    ? heldByRaw === "household_member"
      ? "household_member"
      : "self"
    : "none";

  return {
    isPlus,
    heldBy,
    isLoading: enabled && isFetching && !hasLoaded,
    isRefreshing: isFetching && hasLoaded,
    refetch,
  };
}

// Test-only helper. Exported separately so production callers don't reach
// for it — mirrors the test-isolation pattern callers of `useReadinessStore
// .getState().reset()` use in their own test files.
export function __resetEntitlementsStore(): void {
  useEntitlementsStore.getState().reset();
}
